/**
 * P7 (May 2026 Rhinobill parity): inline 277CA code badge with hover
 * tooltip. Renders the raw STC code (e.g., "F2:1:PR") next to the claim
 * status, and on hover surfaces the full category label + status meaning
 * + entity + recommended next-step action.
 *
 * Data source: the claim's `edi_277_status_code`, `edi_277_status`,
 * `edi_277_claim_status`, and `edi_277_action` fields populated by the
 * EDI response poller from each STC*<cat>:<status>:<entity> segment.
 *
 * The tooltip is keyboard-accessible (focus shows it too).
 */
import React from 'react';
import { formatDateTime } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

// Color tone per outcome category for the badge background. Mirrors
// backend/config/edi_277ca_codes.py outcome enum.
const OUTCOME_COLOR_MAP = {
  received: 'bg-sky-100 text-sky-800 border-sky-200',
  in_process: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  denied: 'bg-red-100 text-red-800 border-red-200',
  complete: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  needs_info: 'bg-amber-100 text-amber-800 border-amber-200',
  system_error: 'bg-slate-100 text-slate-800 border-slate-200',
  default: 'bg-slate-100 text-slate-700 border-slate-200',
};

// Map A-prefix categories to known outcomes for the badge color when
// the API hasn't enriched the claim. Mirrors the canonical CATEGORY_CODES
// map in backend/config/edi_277ca_codes.py.
const CATEGORY_TO_OUTCOME = {
  A0: 'received', A1: 'received', A2: 'in_process', A3: 'rejected',
  A4: 'in_process', A5: 'in_process', A6: 'rejected', A7: 'rejected', A8: 'needs_info',
  E0: 'system_error', E1: 'system_error', E2: 'rejected', E3: 'rejected', E4: 'rejected',
  F0: 'denied', F1: 'in_process', F2: 'rejected', F3: 'in_process',
  F4: 'complete', F5: 'complete',
  P0: 'in_process', P1: 'in_process',
};

const Edi277Badge = ({ claim }) => {
  const stcCode = claim.edi_277_status_code;
  if (!stcCode) return null;

  // Extract the 2-char category prefix (e.g., "F2" from "F2:1:PR")
  const categoryPrefix = (stcCode.split(':')[0] || stcCode).slice(0, 2).toUpperCase();
  const outcome = CATEGORY_TO_OUTCOME[categoryPrefix] || 'default';
  const colorClasses = OUTCOME_COLOR_MAP[outcome] || OUTCOME_COLOR_MAP.default;

  // Tooltip body — combines the parser-extracted strings into a single
  // structured panel.
  const tooltipBody = (
    <div className="max-w-xs text-xs leading-relaxed space-y-1.5">
      <div className="flex items-baseline gap-2">
        <span className="font-mono font-bold">{stcCode}</span>
        <span className="text-[10px] text-slate-300 uppercase tracking-wider">EDI 277CA</span>
      </div>
      {claim.edi_277_status ? (
        <p>
          <span className="text-slate-300">Category: </span>
          <span className="font-medium">{claim.edi_277_status}</span>
        </p>
      ) : null}
      {claim.edi_277_claim_status ? (
        <p>
          <span className="text-slate-300">Status: </span>
          <span className="font-medium">{claim.edi_277_claim_status}</span>
        </p>
      ) : null}
      {claim.edi_277_action ? (
        <p className="pt-1 border-t border-slate-700/50">
          <span className="text-slate-300">Next: </span>
          <span className="font-medium text-emerald-200">{claim.edi_277_action}</span>
        </p>
      ) : null}
      {claim.edi_277_received_at ? (
        <p className="text-[10px] text-slate-400 pt-1">
          Received: {formatDateTime(claim.edi_277_received_at)}
        </p>
      ) : null}
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono cursor-help ${colorClasses}`}
            data-testid={`edi-277-badge-${claim.id}`}
            tabIndex={0}
            aria-label={`277CA code ${stcCode}`}
          >
            {stcCode}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 text-white border-slate-700 px-3 py-2">
          {tooltipBody}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Edi277Badge;
